var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useLayoutEffect, useRef, useState, } from "react";
import { Icon } from "./Icon";
const BG_CLASSES = {
    white: "bg-background-base",
    grey: "bg-background-base",
};
const calcInputStyle = (inputSize, hasLeftIcon) => {
    switch (inputSize) {
        case "md":
            return {
                height: 32,
                paddingTop: 7,
                paddingBottom: 7,
                paddingLeft: hasLeftIcon ? 31 : 7,
                paddingRight: 29,
            };
        case "lg":
            return {
                height: 52,
                paddingTop: 17,
                paddingBottom: 17,
                paddingLeft: hasLeftIcon ? 37 : 11,
                paddingRight: 29,
            };
        default:
            return {};
    }
};
export const Input = forwardRef((_a, ref) => {
    var { label, error, helperText, leftIcon, className = "", disabled = false, bg = "white", inputSize = "md", lightPlaceholder, activeVariant = "default", inputStyle, unit, trailingValue } = _a, props = __rest(_a, ["label", "error", "helperText", "leftIcon", "className", "disabled", "bg", "inputSize", "lightPlaceholder", "activeVariant", "inputStyle", "unit", "trailingValue"]);
    const containerRef = useRef(null);
    const valueRef = useRef(null);
    const suffixRef = useRef(null);
    const trailingRef = useRef(null);
    const [metrics, setMetrics] = useState({
        containerWidth: 0,
        valueWidth: 0,
        suffixWidth: 0,
        trailingWidth: 0,
    });
    const effectiveStyle = Object.assign(Object.assign({}, calcInputStyle(inputSize, !!leftIcon)), inputStyle);
    const inputValue = typeof props.value === "string" || typeof props.value === "number"
        ? String(props.value)
        : "";
    const leftPadding = typeof effectiveStyle.paddingLeft === "number"
        ? effectiveStyle.paddingLeft
        : 8;
    const trailingRight = 8;
    const computedRightPadding = unit || trailingValue
        ? metrics.suffixWidth +
            metrics.trailingWidth +
            (unit || trailingValue ? 12 : 0) +
            trailingRight
        : effectiveStyle.paddingRight;
    const maxSuffixLeft = Math.max(leftPadding, metrics.containerWidth -
        (typeof computedRightPadding === "number" ? computedRightPadding : 8));
    const suffixLeft = Math.min(leftPadding + metrics.valueWidth + 4, maxSuffixLeft);
    useLayoutEffect(() => {
        var _a, _b, _c, _d, _e, _f, _g, _h;
        if (!unit && !trailingValue) {
            return;
        }
        // Re-run measurement when displayed input text changes.
        void inputValue;
        const next = {
            containerWidth: (_b = (_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.offsetWidth) !== null && _b !== void 0 ? _b : 0,
            valueWidth: (_d = (_c = valueRef.current) === null || _c === void 0 ? void 0 : _c.offsetWidth) !== null && _d !== void 0 ? _d : 0,
            suffixWidth: (_f = (_e = suffixRef.current) === null || _e === void 0 ? void 0 : _e.offsetWidth) !== null && _f !== void 0 ? _f : 0,
            trailingWidth: (_h = (_g = trailingRef.current) === null || _g === void 0 ? void 0 : _g.offsetWidth) !== null && _h !== void 0 ? _h : 0,
        };
        setMetrics((prev) => prev.containerWidth === next.containerWidth &&
            prev.valueWidth === next.valueWidth &&
            prev.suffixWidth === next.suffixWidth &&
            prev.trailingWidth === next.trailingWidth
            ? prev
            : next);
    }, [inputValue, trailingValue, unit]);
    return (_jsxs("div", { className: "w-full", children: [label && _jsx("label", { className: "block f-14-400-16 mb-1", children: label }), _jsxs("div", { ref: containerRef, className: "relative", children: [leftIcon && (_jsx("div", { className: "absolute left-[7px] top-1/2 -translate-y-1/2 text-background-neutral-4", children: _jsx(Icon, { name: leftIcon }) })), _jsx("input", Object.assign({ ref: ref, disabled: disabled, style: Object.assign(Object.assign({}, effectiveStyle), { paddingRight: computedRightPadding }), className: `
              w-full
              border rounded-lg
              ${disabled
                            ? "border-border-base-disabled"
                            : error
                                ? "border-red-500 focus:ring-0 focus:border-red-500"
                                : activeVariant === "accent"
                                    ? "border-border-base hover:border-border-base-hover focus:border-button-accent-1 focus:ring-0"
                                    : "border-border-base hover:border-border-base-hover focus:border-border-base-active focus:ring-0"}
              ${disabled ? "bg-background-base-disabled cursor-not-allowed text-text-tertiary" : BG_CLASSES[bg]}
              outline-none tr-all
              ${disabled ? "" : "text-text-primary"}
              ${disabled ? "placeholder:text-text-tertiary" : lightPlaceholder ? "placeholder:text-text-tertiary" : "placeholder:text-text-secondary"}
              f-14-400-16
              ${className}
            ` }, props)), (unit || trailingValue) && (_jsx("span", { ref: valueRef, "aria-hidden": "true", className: "pointer-events-none absolute top-1/2 invisible -translate-y-1/2 whitespace-pre f-14-400-16", style: { left: leftPadding }, children: inputValue })), unit && (_jsx("div", { ref: suffixRef, "aria-hidden": "true", className: "pointer-events-none absolute top-[calc(50%+1px)] flex -translate-y-1/2 items-center whitespace-nowrap", style: { left: suffixLeft }, children: _jsx("span", { className: "f-14-400-16 text-text-tertiary", children: unit }) })), trailingValue ? (_jsx("span", { ref: trailingRef, "aria-hidden": "true", className: "pointer-events-none absolute top-[calc(50%+1px)] -translate-y-1/2 whitespace-nowrap f-14-400-16 text-text-tertiary", style: { right: trailingRight }, children: trailingValue })) : null] }), error && _jsx("p", { className: "mt-1 f-12-500-normal text-red-500", children: error }), helperText && !error && (_jsx("p", { className: "mt-1 f-12-500-normal text-text-secondary", children: helperText }))] }));
});
Input.displayName = "Input";
